// Pure resolver for the search-bias parameter shape sent to Google Places.
// Priority: explicit map viewport > active city > none.

import type { Coordinated } from './group-actions.js';
import type { TripCityView, ViewportRect } from './api.js';

export interface ResolvedBias {
  locationBias?: {
    rectangle: {
      low: { latitude: number; longitude: number };
      high: { latitude: number; longitude: number };
    };
  };
  regionCode?: string;
}

function rectFromViewport(v: ViewportRect): ResolvedBias['locationBias'] {
  return {
    rectangle: {
      low: { latitude: v.south, longitude: v.west },
      high: { latitude: v.north, longitude: v.east },
    },
  };
}

/**
 * True when the point sits inside (or on) the rectangle.
 * Longitude wrap-around (a viewport spanning the antimeridian where
 * `west > east`) is rare for trip cities — Google returns sane viewports
 * for normal localities. We don't handle it.
 */
/** Build a viewport rect of approximately `radiusKm` around a point.
 *  Used to bias autocomplete/text-search to a POI's immediate
 *  neighborhood when the user picks "Search around here". */
export function viewportAroundPoint(
  point: Coordinated,
  radiusKm: number,
): ViewportRect {
  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.cos((point.lat * Math.PI) / 180));
  return {
    north: point.lat + latDelta,
    south: point.lat - latDelta,
    east: point.lng + lngDelta,
    west: point.lng - lngDelta,
  };
}

export function isInViewport(point: Coordinated, v: ViewportRect): boolean {
  return (
    point.lat >= v.south &&
    point.lat <= v.north &&
    point.lng >= v.west &&
    point.lng <= v.east
  );
}

export function resolveBias(input: {
  viewport?: ViewportRect | null;
  activeCity?: TripCityView | null;
}): ResolvedBias {
  // An explicit viewport (from "Search this area") wins. We omit regionCode
  // in that case — the active city's region may not match where the user
  // panned, and a wrong regionCode skews result ranking.
  if (input.viewport) {
    return { locationBias: rectFromViewport(input.viewport) };
  }
  const city = input.activeCity;
  if (!city) return {};
  return {
    locationBias: rectFromViewport(city.viewport),
    regionCode: city.regionCode,
  };
}
